/**
 * The signed-in account, as React sees it.
 *
 * One piece of state matters for routing: `status`. It is three-valued rather than a boolean,
 * because "we have not finished asking yet" is not "signed out" — conflating them redirects a
 * signed-in person to the sign-in screen on every reload, which is the classic version of this bug.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import type { Account } from "../api/types";
import * as session from "./session";

export type AuthStatus = "restoring" | "authenticated" | "anonymous";

export interface AuthContextValue {
  status: AuthStatus;
  account: Account | null;
  signIn: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const [status, setStatus] = useState<AuthStatus>(() =>
    session.hasStoredSession() ? "restoring" : "anonymous",
  );
  const [account, setAccount] = useState<Account | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (status !== "restoring") return;
    let cancelled = false;
    void session
      .restore()
      .then((restored) => {
        if (cancelled) return;
        setAccount(restored);
        setStatus(restored === null ? "anonymous" : "authenticated");
      })
      .catch(() => {
        // Restoring failed for a reason that is not "your session ended" — a network blip, say.
        // Anonymous is the safe landing: the sign-in form will report the real error on submit.
        if (!cancelled) setStatus("anonymous");
      });
    return () => {
      cancelled = true;
    };
    // Runs once, when status is "restoring" on first render.
  }, [status]);

  const handleSignIn = useCallback(async (email: string, password: string) => {
    const next = await session.signIn(email, password);
    setAccount(next);
    setStatus("authenticated");
  }, []);

  const handleRegister = useCallback(async (email: string, password: string) => {
    const next = await session.registerAndSignIn(email, password);
    setAccount(next);
    setStatus("authenticated");
  }, []);

  const handleSignOut = useCallback(async () => {
    await session.signOut();
    setAccount(null);
    setStatus("anonymous");
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      account,
      signIn: handleSignIn,
      register: handleRegister,
      signOut: handleSignOut,
    }),
    [status, account, handleSignIn, handleRegister, handleSignOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (value === null) throw new Error("useAuth must be used inside an AuthProvider");
  return value;
}
